/* ==========================================================================
   CHLOIA — Shoppable video reel
   Videos load and play (muted, looping) only while on screen, one sound at a time.
   Add to cart is handled by the shared quick add in ch-theme.js.
   ========================================================================== */
(() => {
  if (customElements.get('ch-shop-video')) return;

  class ChShopVideo extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-ch-reel-card]'));
      this.videos = Array.from(this.querySelectorAll('video'));
      if (!this.videos.length) return;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;

      this.videos.forEach((video) => {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.removeAttribute('controls');
      });

      // Play only what's on screen; load the file just before it's needed
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                if (video.preload === 'none') video.preload = 'metadata';
                if (this.autoplay) this.play(video);
              } else {
                video.pause();
                if (!video.muted) this.mute(video);
              }
            });
          },
          { threshold: 0.5 }
        );
        this.videos.forEach((v) => this.io.observe(v));
      } else if (this.autoplay) {
        this.videos.forEach((v) => this.play(v));
      }

      this.addEventListener('click', (e) => {
        const soundBtn = e.target.closest('[data-ch-reel-sound]');
        if (soundBtn) {
          e.preventDefault();
          this.toggleSound(soundBtn);
          return;
        }
        // Tapping the video itself plays or pauses it
        const media = e.target.closest('.ch-reel__media');
        if (media) {
          const video = media.querySelector('video');
          if (!video) return;
          if (video.paused) this.play(video);
          else video.pause();
        }
      });

      this.onVisibility = () => {
        if (document.hidden) this.videos.forEach((v) => v.pause());
        else if (this.autoplay) this.videos.forEach((v) => this.playIfVisible(v));
      };
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    play(video) {
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }

    playIfVisible(video) {
      const r = video.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) this.play(video);
    }

    mute(video) {
      video.muted = true;
      const btn = video.closest('.ch-reel__media').querySelector('[data-ch-reel-sound]');
      if (btn) btn.setAttribute('aria-pressed', 'false');
    }

    toggleSound(btn) {
      const video = btn.closest('.ch-reel__media').querySelector('video');
      if (!video) return;
      const turningOn = video.muted;
      // Only one video makes sound at a time
      this.videos.forEach((v) => {
        if (v !== video && !v.muted) this.mute(v);
      });
      video.muted = !turningOn;
      btn.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
      if (turningOn) this.play(video);
    }
  }

  customElements.define('ch-shop-video', ChShopVideo);
})();
